<template>
  <v-app :class="{ 'theme--dark': isDarkMode }">
    <HeaderDealerDashboard
      :userName="userName"
      :businessName="businessName"
      :isDarkModeProp="isDarkMode"
      @navigate="handleNavigation"
    />

    <v-main class="dealer-main" :class="{ 'dark-mode': isDarkMode }">
      <v-container fluid class="pa-6">
        <!-- Page Header -->
        <div class="page-header mb-6">
          <h1 class="text-h4 font-weight-bold text-white mb-0" style="background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%); padding: 20px; border-radius: 8px;">
            <span class="material-icons-round mr-3" style="vertical-align: middle;">people</span>
            Dealer Portal - Referring Agents
          </h1>
        </div>

        <!-- Filters Section -->
        <v-card class="mb-6" elevation="3">
          <v-card-title class="text-h5 font-weight-bold primary--text d-flex justify-space-between align-center">
            <div>
              <span class="material-icons-round mr-2">filter_list</span>
              Filters
            </div>
            <v-btn 
              color="blue" 
              dark
              @click="searchAgents"
              :loading="searchLoading"
              class="text-capitalize"
            >
              <span class="material-icons-round mr-2">search</span>
              Search
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-6">
            <v-row>
              <!-- ZIP Code Filter -->
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="filters.zipCode"
                  label="ZIP Code"
                  outlined
                  dense
                  placeholder="e.g., 85041"
                  :rules="zipCodeRules"
                  maxlength="5"
                  clearable
                >
                  <template v-slot:prepend-inner>
                    <span class="material-icons-round text-grey">location_on</span>
                  </template>
                </v-text-field>
              </v-col>

              <!-- Min Rating Filter -->
              <v-col cols="12" md="4">
                <v-select
                  v-model="filters.minRating"
                  :items="ratingOptions"
                  label="Min Rating"
                  outlined
                  dense
                  clearable
                >
                  <template v-slot:prepend-inner>
                    <span class="material-icons-round text-grey">star</span>
                  </template>
                </v-select>
              </v-col>

              <!-- Min Referrals Filter -->
              <v-col cols="12" md="4">
                <v-select
                  v-model="filters.minReferrals"
                  :items="referralOptions"
                  label="Min Referrals"
                  outlined
                  dense
                  clearable
                >
                  <template v-slot:prepend-inner>
                    <span class="material-icons-round text-grey">group</span>
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <!-- Active Filters Display -->
            <div v-if="hasActiveFilters" class="mt-4">
              <div class="text-subtitle-2 mb-2">Active Filters:</div>
              <v-chip
                v-if="filters.zipCode"
                close
                @click:close="filters.zipCode = ''"
                class="mr-2 mb-2"
                color="primary"
                outlined
              >
                ZIP: {{ filters.zipCode }}
              </v-chip>
              <v-chip
                v-if="filters.minRating"
                close
                @click:close="filters.minRating = null"
                class="mr-2 mb-2"
                color="primary"
                outlined
              >
                Min Rating: {{ filters.minRating }}★+
              </v-chip>
              <v-chip
                v-if="filters.minReferrals"
                close
                @click:close="filters.minReferrals = null"
                class="mr-2 mb-2"
                color="primary"
                outlined
              >
                Referrals: >{{ filters.minReferrals }}
              </v-chip>
            </div>
          </v-card-text>
        </v-card>

        <!-- Agents List -->
        <v-card elevation="3">
          <v-card-title class="text-h5 font-weight-bold primary--text">
            <span class="material-icons-round mr-2">people_outline</span>
            Available Agents
            <v-spacer></v-spacer>
            <span class="text-subtitle-1">{{ filteredAgents.length }} agents found</span>
          </v-card-title>
          <v-card-text class="pa-0">
            <div v-if="loading" class="text-center pa-8">
              <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
              <div class="mt-4 text-h6">Loading agents...</div>
            </div>

            <div v-else-if="filteredAgents.length === 0" class="text-center pa-8">
              <span class="material-icons-round" style="font-size: 64px; color: #ccc;">person_search</span>
              <div class="mt-4 text-h6 text-grey-600">No agents found</div>
              <div class="text-body-2 text-grey-500">Try adjusting your filters</div>
            </div>

            <div v-else>
              <v-list class="pa-0" :style="{ backgroundColor: isDarkMode ? 'transparent' : '#fff' }">
                <template v-for="(agent, index) in filteredAgents">
                  <v-list-item :key="agent.user_id" class="agent-item pa-6">
                    <v-list-item-content>
                      <div class="d-flex justify-space-between align-start">
                        <div class="agent-info">
                          <v-list-item-title
                            class="text-h6 font-weight-bold mb-1"
                            :style="{ color: isDarkMode ? '#e0e0e0' : '#212121' }"
                          >
                            {{ agent.name }}
                          </v-list-item-title>
                          <v-list-item-subtitle
                            class="text-body-2 mb-2"
                            :style="{ color: isDarkMode ? '#b0b0b0' : '#616161' }"
                          >
                            <span class="material-icons-round mr-1" :style="{ fontSize: '16px', color: isDarkMode ? '#b0b0b0' : '#757575' }">location_on</span>
                            {{ agent.location }}
                          </v-list-item-subtitle>

                          <div class="agent-stats d-flex align-center">
                            <!-- Rating -->
                            <div class="mr-4">
                              <v-rating
                                :value="agent.rating"
                                color="amber"
                                dense
                                half-increments
                                readonly
                                size="16"
                              ></v-rating>
                              <span class="text-caption ml-1" :style="{ color: isDarkMode ? '#b0b0b0' : '#757575' }">({{ agent.reviewCount }} reviews)</span>
                            </div>

                            <!-- Referrals -->
                            <div class="text-body-2" :style="{ color: isDarkMode ? '#b0b0b0' : '#616161' }">
                              <span class="material-icons-round mr-1" :style="{ fontSize: '16px', color: isDarkMode ? '#b0b0b0' : '#757575' }">group</span>
                              {{ agent.referralCount }} referrals
                            </div>
                          </div>
                        </div>

                        <div class="agent-actions">
                          <v-btn
                            color="blue"
                            dark
                            @click="addAgent(agent)"
                            :loading="agent.adding"
                            :disabled="agent.isAdded"
                            class="text-capitalize"
                          >
                            <span class="material-icons-round mr-2">
                              {{ agent.isAdded ? 'check' : 'person_add' }}
                            </span>
                            {{ agent.isAdded ? 'Added' : 'Add Agent' }}
                          </v-btn>
                        </div>
                      </div>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider v-if="index < filteredAgents.length - 1"></v-divider>
                </template>
              </v-list>

              <!-- Load More Button -->
              <div v-if="hasMoreAgents" class="text-center pa-4">
                <v-btn
                  color="primary"
                  outlined
                  @click="loadMoreAgents"
                  :loading="loadingMore"
                  class="text-capitalize"
                >
                  <span class="material-icons-round mr-2">expand_more</span>
                  Load More Agents
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Assigned Agents Section -->
        <v-card v-if="assignedAgents.length > 0" elevation="3" class="mt-6">
          <v-card-title class="text-h5 font-weight-bold success--text">
            <span class="material-icons-round mr-2">assignment_ind</span>
            Assigned Agents
            <v-spacer></v-spacer>
            <span class="text-subtitle-1">{{ assignedAgents.length }} agents assigned</span>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-list class="pa-0" :style="{ backgroundColor: isDarkMode ? 'transparent' : '#fff' }">
              <template v-for="(agent, index) in assignedAgents">
                <v-list-item :key="agent.user_id" class="agent-item pa-6">
                  <v-list-item-content>
                    <div class="d-flex justify-space-between align-start">
                      <div class="agent-info">
                        <v-list-item-title
                          class="text-h6 font-weight-bold mb-1"
                          :style="{ color: isDarkMode ? '#e0e0e0' : '#212121' }"
                        >
                          {{ agent.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle
                          class="text-body-2 mb-2"
                          :style="{ color: isDarkMode ? '#b0b0b0' : '#616161' }"
                        >
                          <span class="material-icons-round mr-1" :style="{ fontSize: '16px', color: isDarkMode ? '#b0b0b0' : '#757575' }">location_on</span>
                          {{ agent.location }}
                        </v-list-item-subtitle>

                        <div class="agent-stats d-flex align-center">
                          <!-- Rating -->
                          <div class="mr-4">
                            <v-rating
                              :value="agent.rating"
                              color="amber"
                              dense
                              half-increments
                              readonly
                              size="16"
                            ></v-rating>
                            <span class="text-caption ml-1" :style="{ color: isDarkMode ? '#b0b0b0' : '#757575' }">({{ agent.reviewCount }} reviews)</span>
                          </div>

                          <!-- Referrals -->
                          <div class="text-body-2" :style="{ color: isDarkMode ? '#b0b0b0' : '#616161' }">
                            <span class="material-icons-round mr-1" :style="{ fontSize: '16px', color: isDarkMode ? '#b0b0b0' : '#757575' }">group</span>
                            {{ agent.referralCount }} referrals
                          </div>
                        </div>
                      </div>

                      <div class="agent-actions d-flex align-center">
                        <v-chip color="success" dark class="mr-3">
                          <span class="material-icons-round mr-1" style="font-size: 16px;">check_circle</span>
                          Assigned
                        </v-chip>
                        <v-btn
                          color="error"
                          outlined
                          small
                          @click="unassignAgent(agent)"
                          class="text-capitalize"
                        >
                          <span class="material-icons-round mr-1" style="font-size: 16px;">person_remove</span>
                          Unassign
                        </v-btn>
                      </div>
                    </div>
                  </v-list-item-content>
                </v-list-item>
                <v-divider v-if="index < assignedAgents.length - 1"></v-divider>
              </template>
            </v-list>
          </v-card-text>
        </v-card>
      </v-container>
    </v-main>

    <FooterDealer />

    <!-- Add Agent Confirmation Dialog -->
    <v-dialog v-model="showAddDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">Add Referring Agent</v-card-title>
        <v-card-text>
          <p>Are you sure you want to add <strong>{{ selectedAgent && selectedAgent.name }}</strong> as a referring agent?</p>
          <p class="text-body-2 text-grey-600 mt-2">
            This agent will be able to refer customers to your dealership and earn commissions.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showAddDialog = false">Cancel</v-btn>
          <v-btn color="blue" dark @click="confirmAddAgent" :loading="confirmLoading">Add Agent</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Unassign Agent Confirmation Dialog -->
    <v-dialog v-model="showUnassignDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6 error--text">Unassign Referring Agent</v-card-title>
        <v-card-text>
          <p>Are you sure you want to unassign <strong>{{ selectedAgent && selectedAgent.name }}</strong> from your dealership?</p>
          <p class="text-body-2 text-grey-600 mt-2">
            This agent will no longer be associated with your dealership and will become available for other dealers.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showUnassignDialog = false">Cancel</v-btn>
          <v-btn color="error" dark @click="confirmUnassignAgent" :loading="confirmLoading">Unassign</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import HeaderDealerDashboard from '@/components/Layout/HeaderDealerDashboard.vue'
import FooterDealer from '@/components/Layout/FooterDealer.vue'

export default {
  name: 'DealerReferringAgents',
  components: {
    HeaderDealerDashboard,
    FooterDealer
  },
  data() {
    return {
      isDarkMode: true,
      loading: false,
      searchLoading: false,
      loadingMore: false,
      confirmLoading: false,
      userName: '',
      businessName: '',
      showAddDialog: false,
      showUnassignDialog: false,
      selectedAgent: null,
      currentPage: 1,
      hasMoreAgents: true,
      filters: {
        zipCode: '',
        minRating: null,
        minReferrals: null
      },
      availableAgents: [],
      assignedAgents: [],
      ratingOptions: [
        { text: '1★ and above', value: 1 },
        { text: '2★ and above', value: 2 },
        { text: '3★ and above', value: 3 },
        { text: '4★ and above', value: 4 },
        { text: '5★ only', value: 5 }
      ],
      referralOptions: [
        { text: 'More than 5', value: 5 },
        { text: 'More than 10', value: 10 },
        { text: 'More than 20', value: 20 },
        { text: 'More than 50', value: 50 }
      ],
      zipCodeRules: [
        v => !v || /^\d{5}$/.test(v) || 'ZIP code must be 5 digits'
      ]
    }
  },
  computed: {
    filteredAgents() {
      // Filtering is now handled by the backend, so just return availableAgents
      return this.availableAgents
    },
    hasActiveFilters() {
      return this.filters.zipCode || this.filters.minRating || this.filters.minReferrals
    }
  },
  beforeCreate() {
    // Check authentication
    if (!localStorage.getItem('token') || localStorage.getItem('user_flag') !== 'dealer') {
      this.$router.push('/dealers/login')
    }
  },
  async mounted() {
    this.loadDarkModePreference()
    await this.loadUserData()
    await this.loadAgents()
  },
  methods: {
    loadDarkModePreference() {
      const saved = localStorage.getItem('dealer_dark_mode')
      this.isDarkMode = saved !== null ? saved === 'true' : true
    },
    loadUserData() {
      this.userName = localStorage.getItem('user_name') || 'Dealer'
      this.businessName = localStorage.getItem('business_name') || 'Business'
    },
    async loadAgents(reset = true) {
      try {
        if (reset) {
          this.loading = true
          this.currentPage = 1
          this.agents = []
        } else {
          this.loadingMore = true
        }

        const token = localStorage.getItem('token')
        
        const response = await axios.get('/api/dealer/available-agents', {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          params: {
            page: this.currentPage,
            ...this.filters
          }
        })

        if (response.data.flag === 1) {
          const newAvailableAgents = response.data.data.available_agents || []
          const assignedAgents = response.data.data.assigned_agents || []

          if (reset) {
            this.availableAgents = newAvailableAgents
            this.assignedAgents = assignedAgents
          } else {
            this.availableAgents.push(...newAvailableAgents)
          }
          this.hasMoreAgents = response.data.data.has_more || false
          this.currentPage++
        }
      } catch (error) {
        console.error('Error loading agents:', error)
        this.$toast.error('Error loading agents')
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },
    handleNavigation(item) {
      // Navigation is handled by the HeaderDealerDashboard component
    },
    async searchAgents() {
      this.searchLoading = true
      await this.loadAgents(true)
      this.searchLoading = false
    },
    async loadMoreAgents() {
      await this.loadAgents(false)
    },
    addAgent(agent) {
      this.selectedAgent = agent
      this.showAddDialog = true
    },
    unassignAgent(agent) {
      this.selectedAgent = agent
      this.showUnassignDialog = true
    },
    async confirmAddAgent() {
      try {
        this.confirmLoading = true
        const token = localStorage.getItem('token')
        
        const response = await axios.post('/api/dealer/add-referring-agent', {
          agent_id: this.selectedAgent.user_id
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.$toast.success(`${this.selectedAgent.name} added as referring agent!`)

          // Move agent from available to assigned
          const agentIndex = this.availableAgents.findIndex(agent => agent.user_id === this.selectedAgent.user_id)
          if (agentIndex !== -1) {
            const agent = this.availableAgents.splice(agentIndex, 1)[0]
            agent.isAdded = true
            this.assignedAgents.push(agent)
          }

          this.showAddDialog = false
        } else {
          this.$toast.error(response.data.message || 'Error adding agent')
        }
      } catch (error) {
        console.error('Error adding agent:', error)
        this.$toast.error('Error adding referring agent')
      } finally {
        this.confirmLoading = false
      }
    },
    async confirmUnassignAgent() {
      try {
        this.confirmLoading = true
        const token = localStorage.getItem('token')

        const response = await axios.post('/api/dealer/unassign-referring-agent', {
          agent_id: this.selectedAgent.user_id
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.$toast.success(`${this.selectedAgent.name} unassigned successfully!`)

          // Move agent from assigned back to available
          const agentIndex = this.assignedAgents.findIndex(agent => agent.user_id === this.selectedAgent.user_id)
          if (agentIndex !== -1) {
            const agent = this.assignedAgents.splice(agentIndex, 1)[0]
            agent.isAdded = false
            this.availableAgents.unshift(agent) // Add to beginning of available list
          }

          this.showUnassignDialog = false
        } else {
          this.$toast.error(response.data.message || 'Error unassigning agent')
        }
      } catch (error) {
        console.error('Error unassigning agent:', error)
        this.$toast.error('Error unassigning referring agent')
      } finally {
        this.confirmLoading = false
      }
    }
  }
}
</script>

<style scoped>
.dealer-main {
  background-color: #f5f5f5;
  min-height: 100vh;
}
.dealer-main.dark-mode {
  background: url('~@/assets/img/skyline-bg-dark.png') no-repeat center bottom;
  background-size: cover;
  background-color: #0f0f23;
}

.page-header h1 {
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.agent-item {
  transition: background-color 0.3s ease;
}

.agent-item:hover {
  background-color: #f8f9fa;
}

.agent-info {
  flex: 1;
}

.agent-stats {
  margin-top: 8px;
}

.agent-actions {
  margin-left: 16px;
}

.v-card {
  border-radius: 12px !important;
}

.v-card-title {
  padding: 20px 24px 16px 24px;
}

.v-card-text {
  padding: 0 24px 24px 24px;
}

.v-list-item {
  border-radius: 0 !important;
}
</style>

<style>
/* Light mode - ensure text visibility */
.dealer-main:not(.dark-mode) .v-list-item__title,
.dealer-main:not(.dark-mode) .v-list-item__subtitle,
.dealer-main:not(.dark-mode) .v-list-item__content,
.dealer-main:not(.dark-mode) .agent-info,
.dealer-main:not(.dark-mode) .agent-info *,
.dealer-main:not(.dark-mode) .agent-stats,
.dealer-main:not(.dark-mode) .agent-stats *,
.dealer-main:not(.dark-mode) .text-h6,
.dealer-main:not(.dark-mode) .text-subtitle-1,
.dealer-main:not(.dark-mode) .text-body-2,
.dealer-main:not(.dark-mode) .text-caption {
  color: #424242 !important;
}
.dealer-main:not(.dark-mode) .v-icon,
.dealer-main:not(.dark-mode) .material-icons-round {
  color: #757575 !important;
}
.dealer-main:not(.dark-mode) .v-btn .v-icon,
.dealer-main:not(.dark-mode) .v-btn .material-icons-round {
  color: inherit !important;
}

/* Dark mode styles - unscoped for proper cascading */
.dark-mode .v-card {
  background: rgba(30, 30, 60, 0.8) !important;
  border: 1px solid rgba(138, 43, 226, 0.3);
}
/* Text colors */
.dark-mode .v-card-title,
.dark-mode .v-card-text,
.dark-mode .v-card-text *,
.dark-mode .v-list-item__title,
.dark-mode .v-list-item__subtitle,
.dark-mode .v-list-item__content,
.dark-mode .agent-info,
.dark-mode .agent-info *,
.dark-mode .agent-stats,
.dark-mode .agent-stats *,
.dark-mode .text-h4,
.dark-mode .text-h5,
.dark-mode .text-h6,
.dark-mode .text-subtitle-1,
.dark-mode .text-subtitle-2,
.dark-mode .text-body-1,
.dark-mode .text-body-2,
.dark-mode .text-caption,
.dark-mode strong,
.dark-mode label,
.dark-mode .v-label,
.dark-mode .v-messages__message,
.dark-mode .v-input__slot,
.dark-mode .v-text-field input,
.dark-mode .v-textarea textarea,
.dark-mode .v-select__selection,
.dark-mode .v-radio .v-label,
.dark-mode .v-checkbox .v-label,
.dark-mode .v-switch .v-label {
  color: #e0e0e0 !important;
}
/* Icons */
.dark-mode .v-icon,
.dark-mode .material-icons,
.dark-mode .material-icons-round,
.dark-mode .material-icons-outlined {
  color: #e0e0e0 !important;
}
/* Button icons should keep their color */
.dark-mode .v-btn .v-icon,
.dark-mode .v-btn .material-icons,
.dark-mode .v-btn .material-icons-round {
  color: inherit !important;
}
/* Colored text overrides */
.dark-mode .v-card-title.primary--text {
  color: #42a5f5 !important;
}
.dark-mode .primary--text {
  color: #42a5f5 !important;
}
.dark-mode .agent-item:hover {
  background-color: rgba(138, 43, 226, 0.1);
}
</style>
